import React, { useState, useMemo } from "react";
import { Task, TimeEntry } from "../../types/Work";
import { Button, Form, Dropdown, Badge } from "react-bootstrap";

type DateFilter =
  | "today"
  | "yesterday"
  | "week"
  | "lastWeek"
  | "month"
  | "lastMonth"
  | "custom"
  | "all";

interface TimeEntriesSectionProps {
  tasks: Task[];
  onUpdateTimeEntry: (
    taskId: string,
    entryId: string,
    updates: Partial<TimeEntry>,
  ) => void;
  onDeleteTimeEntry: (taskId: string, entryId: string) => void;
  onViewTask: (task: Task) => void;
}

interface TimeEntryWithTask extends TimeEntry {
  taskId: string;
  taskName: string;
}

interface DayGroup {
  date: string;
  displayDate: string;
  entries: TimeEntryWithTask[];
  totalMinutes: number;
}

const localDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const dateFromKey = (key: string) => {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const TimeEntriesSection: React.FC<TimeEntriesSectionProps> = ({
  tasks,
  onUpdateTimeEntry,
  onDeleteTimeEntry,
  onViewTask,
}) => {
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<TimeEntry>>({});
  const [durationInput, setDurationInput] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<DateFilter>("week");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());

  const formatDurationInput = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  };

  const parseDurationInput = (value: string): number | null => {
    if (!value) return null;
    const normalized = value.trim().toLowerCase();
    if (!normalized) return null;

    const match = normalized.match(/^(?:(\d+(?:\.\d+)?)h)?\s*(?:(\d+)m)?$/);
    if (!match || !(match[1] || match[2])) {
      return null;
    }

    const hours = match[1] ? parseFloat(match[1]) : 0;
    const minutes = match[2] ? parseInt(match[2]) : 0;

    return Math.round(hours * 60 + minutes);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const calculateDuration = (startAt: Date, endAt: Date | null) => {
    const start = startAt.getTime();
    const end = endAt ? endAt.getTime() : Date.now();
    return Math.floor((end - start) / 1000 / 60);
  };

  const getDateRange = (): { start: Date; end: Date } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilter) {
      case "today":
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return { start: today, end: tomorrow };
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: today };
      case "week":
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return { start: weekStart, end: new Date() };
      case "lastWeek":
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay());
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 7);
        return { start: lastWeekStart, end: lastWeekEnd };
      case "month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start: monthStart, end: new Date() };
      case "lastMonth":
        const lastMonthStart = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1,
        );
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start: lastMonthStart, end: lastMonthEnd };
      case "custom":
        if (customStartDate && customEndDate && customStartDate <= customEndDate) {
          const customEnd = dateFromKey(customEndDate);
          customEnd.setDate(customEnd.getDate() + 1);
          return {
            start: dateFromKey(customStartDate),
            end: customEnd,
          };
        }
        return { start: new Date(0), end: new Date(0) };
      case "all":
      default:
        return { start: new Date(0), end: new Date() };
    }
  };

  const { start: filterStart, end: filterEnd } = getDateRange();

  const formatDayHeader = (date: Date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const entryDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    if (entryDate.getTime() === today.getTime()) {
      return "Today";
    } else if (entryDate.getTime() === yesterday.getTime()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      });
    }
  };

  // Collect and filter all time entries
  const allEntries: TimeEntryWithTask[] = useMemo(() => {
    return tasks.flatMap((task) =>
      (task.timeEntries || [])
        .filter((entry) => {
          const entryDate = new Date(entry.startAt);
          return entryDate >= filterStart && entryDate < filterEnd;
        })
        .map((entry) => ({
          ...entry,
          taskId: task.id,
          taskName: task.name,
        })),
    );
  }, [tasks, filterStart, filterEnd]);

  // Group entries by day
  const groupedByDay: DayGroup[] = useMemo(() => {
    const groups: { [key: string]: TimeEntryWithTask[] } = {};

    allEntries.forEach((entry) => {
      const date = new Date(entry.startAt);
      const dateKey = localDateKey(date);

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(entry);
    });

    return Object.entries(groups)
      .map(([dateKey, entries]) => {
        const date = dateFromKey(dateKey);
        const totalMinutes = entries.reduce(
          (sum, e) => sum + (e.durationMinutes || 0),
          0,
        );

        return {
          date: dateKey,
          displayDate: formatDayHeader(date),
          entries: entries.sort(
            (a, b) =>
              new Date(b.startAt).getTime() - new Date(a.startAt).getTime(),
          ),
          totalMinutes,
        };
      })
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [allEntries]);

  // Get days with entries for calendar highlighting
  const daysWithEntries = useMemo(() => {
    return new Set(
      tasks.flatMap((task) =>
        (task.timeEntries || []).map((entry) => {
          const date = new Date(entry.startAt);
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0",
          )}-${String(date.getDate()).padStart(2, "0")}`;
        }),
      ),
    );
  }, [tasks]);

  const renderCalendar = () => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(
      today.getMonth() + 1,
    ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    // Empty cells before the first day
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(
        day,
      ).padStart(2, "0")}`;
      const hasEntries = daysWithEntries.has(dateStr);
      const isToday = dateStr === todayStr;

      days.push(
        <div
          key={day}
          className={`calendar-day ${hasEntries ? "has-entries" : ""} ${
            isToday ? "today" : ""
          }`}
          onClick={() => {
            if (hasEntries) {
              setCustomStartDate(dateStr);
              setCustomEndDate(dateStr);
              setDateFilter("custom");
            }
          }}
        >
          {day}
        </div>,
      );
    }

    return days;
  };

  const previousMonth = () => {
    setSelectedMonth(
      new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    setSelectedMonth(
      new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1),
    );
  };

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "week":
        return "This Week";
      case "lastWeek":
        return "Last Week";
      case "month":
        return "This Month";
      case "lastMonth":
        return "Last Month";
      case "custom":
        return "Custom Range";
      case "all":
        return "All Time";
      default:
        return "Filter";
    }
  };

  const startEditingEntry = (entry: TimeEntryWithTask) => {
    setEditingEntryId(entry.id);
    setEditForm({
      startAt: entry.startAt,
      endAt: entry.endAt,
      durationMinutes: entry.durationMinutes,
      note: entry.note,
    });
    setDurationInput(formatDurationInput(entry.durationMinutes));
  };

  const saveEditingEntry = (entry: TimeEntryWithTask) => {
    if (!editingEntryId) return;

    // If start or end changed, recalculate duration
    let updates = { ...editForm };
    if (editForm.startAt || editForm.endAt !== undefined) {
      const startAt = editForm.startAt || entry.startAt;
      const endAt = editForm.endAt !== undefined ? editForm.endAt : entry.endAt;
      if (endAt) {
        updates.durationMinutes = calculateDuration(startAt, endAt);
      }
    }

    onUpdateTimeEntry(entry.taskId, entry.id, updates);
    setEditingEntryId(null);
    setEditForm({});
    setDurationInput("");
  };

  const cancelEditing = () => {
    setEditingEntryId(null);
    setEditForm({});
    setDurationInput("");
  };

  const totalMinutes = groupedByDay.reduce(
    (sum, day) => sum + day.totalMinutes,
    0,
  );
  const totalHours = Math.floor(totalMinutes / 60);
  const totalMins = totalMinutes % 60;
  const hasAnyEntries = tasks.some((task) => (task.timeEntries || []).length > 0);
  const needsCustomDates = dateFilter === "custom" &&
    (!customStartDate || !customEndDate || customStartDate > customEndDate);

  return (
    <div className="time-entries-section">
      <div className="time-entries-header">
        <div>
          <h3>
            <i className="fas fa-clock-rotate-left"></i>
            Time Entries
          </h3>
          <p className="time-entries-subtitle">
            {allEntries.length} {allEntries.length === 1 ? "entry" : "entries"}{" "}
            • {groupedByDay.length} {groupedByDay.length === 1 ? "day" : "days"}
          </p>
        </div>
        <div className="time-entries-total">
          <span className="total-label">Total Time</span>
          <span className="total-value">
            {totalHours}h {totalMins}m
          </span>
        </div>
      </div>

      <div className="time-entries-controls">
        <div className="time-entries-filters">
          <div
            className="time-entries-quick-filters"
            aria-label="Quick date filters"
          >
            {([
              ["today", "Today"],
              ["week", "This Week"],
              ["month", "This Month"],
              ["all", "All Time"],
            ] as const).map(([filter, label]) => (
              <button
                key={filter}
                type="button"
                className={dateFilter === filter ? "active" : ""}
                aria-pressed={dateFilter === filter}
                onClick={() => setDateFilter(filter)}
              >
                {label}
              </button>
            ))}
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary" size="sm">
              <i className="fas fa-filter me-2"></i>
              {getFilterLabel()}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                active={dateFilter === "today"}
                onClick={() => setDateFilter("today")}
              >
                Today
              </Dropdown.Item>
              <Dropdown.Item
                active={dateFilter === "yesterday"}
                onClick={() => setDateFilter("yesterday")}
              >
                Yesterday
              </Dropdown.Item>
              <Dropdown.Item
                active={dateFilter === "week"}
                onClick={() => setDateFilter("week")}
              >
                This Week
              </Dropdown.Item>
              <Dropdown.Item
                active={dateFilter === "lastWeek"}
                onClick={() => setDateFilter("lastWeek")}
              >
                Last Week
              </Dropdown.Item>
              <Dropdown.Item
                active={dateFilter === "month"}
                onClick={() => setDateFilter("month")}
              >
                This Month
              </Dropdown.Item>
              <Dropdown.Item
                active={dateFilter === "lastMonth"}
                onClick={() => setDateFilter("lastMonth")}
              >
                Last Month
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                active={dateFilter === "custom"}
                onClick={() => setDateFilter("custom")}
              >
                <i className="fa fa-solid fa-calendar-days me-2"></i>
                Custom Range
              </Dropdown.Item>
              <Dropdown.Item
                active={dateFilter === "all"}
                onClick={() => setDateFilter("all")}
              >
                All Time
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          {dateFilter === "custom" && (
            <div className="custom-date-range">
              <div className="date-input-wrapper">
                <label htmlFor="start-date" className="date-label">
                  From
                </label>
                <Form.Control
                  id="start-date"
                  type="date"
                  size="sm"
                  max={customEndDate || undefined}
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div className="date-separator">
                <i className="fas fa-arrow-right"></i>
              </div>
              <div className="date-input-wrapper">
                <label htmlFor="end-date" className="date-label">
                  To
                </label>
                <Form.Control
                  id="end-date"
                  type="date"
                  size="sm"
                  min={customStartDate || undefined}
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {allEntries.length === 0 ? (
        <div className="time-entries-empty">
          <i className="fas fa-clock"></i>
          <p>
            {needsCustomDates
              ? "Choose a date range"
              : hasAnyEntries
              ? "No entries in this range"
              : "Your time entries will appear here"}
          </p>
          <small>
            {needsCustomDates
              ? "Select a start and end date above to see your entries."
              : hasAnyEntries
              ? "Choose another range to see your logged work."
              : "Start a timer on an In Progress task to begin tracking time."}
          </small>
          {hasAnyEntries && dateFilter !== "all" && !needsCustomDates && (
            <button type="button" onClick={() => setDateFilter("all")}>
              View all time entries
            </button>
          )}
        </div>
      ) : (
        <div className="time-entries-by-day">
          {groupedByDay.map((dayGroup) => (
            <div key={dayGroup.date} className="day-group">
              <div className="day-group-header">
                <div className="day-group-title">
                  <i className="fas fa-calendar-day me-2"></i>
                  <span>{dayGroup.displayDate}</span>
                  <span className="day-group-date">
                    {dateFromKey(dayGroup.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <div className="day-group-total">
                  <i className="fas fa-clock me-1"></i>
                  {formatDuration(dayGroup.totalMinutes)}
                </div>
              </div>

              <div className="day-group-entries">
                {dayGroup.entries.map((entry) => {
                  const isEditing = editingEntryId === entry.id;
                  const task = tasks.find((t) => t.id === entry.taskId);

                  return (
                    <div key={entry.id} className="time-entry-item-compact">
                      {isEditing ? (
                        <div className="time-entry-edit-inline">
                          <div className="edit-row">
                            <Form.Control
                              type="time"
                              size="sm"
                              value={
                                editForm.startAt
                                  ? new Date(editForm.startAt).toLocaleTimeString(
                                      "en-US",
                                      {
                                        hour12: false,
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      },
                                    )
                                  : new Date(entry.startAt).toLocaleTimeString(
                                      "en-US",
                                      {
                                        hour12: false,
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      },
                                    )
                              }
                              onChange={(e) => {
                                const [hours, minutes] =
                                  e.target.value.split(":");
                                const newDate = new Date(
                                  editForm.startAt || entry.startAt,
                                );
                                newDate.setHours(
                                  parseInt(hours),
                                  parseInt(minutes),
                                );
                                const durationMinutes =
                                  editForm.durationMinutes ??
                                  entry.durationMinutes;
                                const nextEnd =
                                  durationMinutes !== undefined
                                    ? new Date(
                                        newDate.getTime() +
                                          durationMinutes * 60000,
                                      )
                                    : (editForm.endAt ?? entry.endAt);

                                setEditForm({
                                  ...editForm,
                                  startAt: newDate,
                                  endAt: nextEnd ?? undefined,
                                });
                              }}
                              className="time-input"
                            />
                            <span className="mx-2">→</span>
                            <Form.Control
                              type="time"
                              size="sm"
                              value={
                                editForm.endAt !== undefined && editForm.endAt
                                  ? new Date(editForm.endAt).toLocaleTimeString(
                                      "en-US",
                                      {
                                        hour12: false,
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      },
                                    )
                                  : entry.endAt
                                    ? new Date(entry.endAt).toLocaleTimeString(
                                        "en-US",
                                        {
                                          hour12: false,
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        },
                                      )
                                    : ""
                              }
                              onChange={(e) => {
                                if (e.target.value) {
                                  const [hours, minutes] =
                                    e.target.value.split(":");
                                  const newDate = new Date(
                                    editForm.endAt ||
                                      entry.endAt ||
                                      entry.startAt,
                                  );
                                  newDate.setHours(
                                    parseInt(hours),
                                    parseInt(minutes),
                                  );
                                  const startDate =
                                    editForm.startAt || entry.startAt;
                                  const durationMinutes = calculateDuration(
                                    startDate,
                                    newDate,
                                  );
                                  setEditForm({
                                    ...editForm,
                                    endAt: newDate,
                                    durationMinutes,
                                  });
                                  setDurationInput(
                                    formatDurationInput(durationMinutes),
                                  );
                                }
                              }}
                              className="time-input"
                            />
                            <Form.Control
                              type="text"
                              size="sm"
                              className="duration-input"
                              placeholder="e.g. 1h30m"
                              value={durationInput}
                              onChange={(e) => {
                                const value = e.target.value;
                                setDurationInput(value);
                                const minutes = parseDurationInput(value);
                                if (minutes !== null) {
                                  const startDate =
                                    editForm.startAt || entry.startAt;
                                  const nextEnd = new Date(startDate);
                                  nextEnd.setMinutes(
                                    nextEnd.getMinutes() + minutes,
                                  );
                                  setEditForm({
                                    ...editForm,
                                    durationMinutes: minutes,
                                    endAt: nextEnd,
                                  });
                                }
                              }}
                            />
                          </div>
                          <div className="edit-row">
                            <button
                              className="task-name-link-small"
                              onClick={() => task && onViewTask(task)}
                            >
                              {entry.taskName}
                            </button>
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="Add a note..."
                              value={
                                editForm.note !== undefined
                                  ? editForm.note || ""
                                  : entry.note || ""
                              }
                              onChange={(e) =>
                                setEditForm({ ...editForm, note: e.target.value })
                              }
                              className="note-input"
                            />
                          </div>
                          <div className="edit-actions">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => saveEditingEntry(entry)}
                            >
                              <i className="fas fa-check me-1"></i>
                              Save
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={cancelEditing}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="time-entry-display-compact">
                          <div className="entry-time-row">
                            <div className="entry-time">
                              <i className="fas fa-clock me-2 text-muted"></i>
                              <span className="time-range">
                                {new Date(entry.startAt).toLocaleTimeString(
                                  "en-US",
                                  { hour: "2-digit", minute: "2-digit" },
                                )}
                                {entry.endAt && (
                                  <>
                                    <i className="fas fa-arrow-right mx-2"></i>
                                    {new Date(entry.endAt).toLocaleTimeString(
                                      "en-US",
                                      { hour: "2-digit", minute: "2-digit" },
                                    )}
                                  </>
                                )}
                                {!entry.endAt && (
                                  <Badge bg="success" className="ms-2">
                                    <i
                                      className="fas fa-circle me-1"
                                      style={{ fontSize: "0.5rem" }}
                                    ></i>
                                    Running
                                  </Badge>
                                )}
                              </span>
                            </div>
                            <div className="entry-duration">
                              <span className="duration-badge-compact">
                                {formatDuration(entry.durationMinutes || 0)}
                              </span>
                            </div>
                          </div>
                          <div className="entry-task-row">
                            <button
                              className="task-name-link-compact"
                              onClick={() => task && onViewTask(task)}
                            >
                              <i className="fas fa-tasks me-2"></i>
                              {entry.taskName}
                            </button>
                            {entry.note && (
                              <div className="entry-note-compact">
                                <i className="fas fa-note-sticky me-2"></i>
                                <span>{entry.note}</span>
                              </div>
                            )}
                          </div>
                          <div className="entry-actions-compact">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-1"
                              onClick={() => startEditingEntry(entry)}
                              title="Edit"
                            >
                              <i className="fas fa-pencil"></i>
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-1 text-danger"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Are you sure you want to delete this time entry?",
                                  )
                                ) {
                                  onDeleteTimeEntry(entry.taskId, entry.id);
                                }
                              }}
                              title="Delete"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
